/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import Disposable from '../../lang/Disposable';
import DisposableList from '../../lang/DisposableList';
import assertUnreachable from '../../lang/assertUnreachable';
import FeatureEnablement from '../../environment/FeatureEnablement';
import {IStreamTrackTransform} from '../transformation/StreamTrackTransform';
import StreamTransformContext, {StreamTransformContextTrack} from '../context/StreamTransformContext';
import TransformStreamPipelineWorker from './worker/TransformStreamPipeline.worker';
import {TransformMessageType} from './worker/TransformStreamPipelineWorkerMessage';
import TransformMessageRouter from './worker/MessageRouter';

type MediaStreamTrackKind = 'video' | 'audio';

export default class InsertableStreams {
  static createEncodedStreamPipeline(streamTransformContext: StreamTransformContext): DisposableList {
    const disposables: DisposableList = new DisposableList();
    const {videoTrack, audioTrack} = streamTransformContext;
    const createTransformPipelineWorkerForTrack = (context: StreamTransformContextTrack) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - Using Webpack's worker loader to inline worker
      const worker = context.transformPipelineWorker = new TransformStreamPipelineWorker();

      disposables.add(new Disposable(() => worker.terminate()));
      disposables.add(context.transformMessageRouter = new TransformMessageRouter(worker));
    };

    const {receiver: videoTrackReceiver, mediaStreamTrack: videoTrackMediaStreamTrack} = videoTrack;

    if (videoTrackReceiver && videoTrackMediaStreamTrack) {
      createTransformPipelineWorkerForTrack(videoTrack);
    }

    const {receiver: audioTrackReceiver, mediaStreamTrack: audioTrackMediaStreamTrack} = audioTrack;

    if (audioTrackReceiver && audioTrackMediaStreamTrack) {
      createTransformPipelineWorkerForTrack(audioTrack);
    }

    return disposables;
  }

  static async configureEncodedStreamPipeline(streamTransformContext: StreamTransformContext): Promise<void> {
    const {videoTrack, audioTrack} = streamTransformContext;
    const addTrackTransforms = (track: StreamTransformContextTrack) => {
      if (track.transformPipelineWorker) {
        if (track.transformFunctionNames.length) {
          for (const transformFunctionName of track.transformFunctionNames) {
            track.transformPipelineWorker.postMessage({
              type: TransformMessageType.AddTransform,
              data: transformFunctionName
            });
          }
        } else {
          // Required for safari to properly work when only video transformation is added (i.e. FrameTimingInformation)
          track.transformPipelineWorker.postMessage({
            type: TransformMessageType.AddTransform,
            data: 'pass-through'
          });
        }
      }
    };

    addTrackTransforms(videoTrack);
    addTrackTransforms(audioTrack);

    const configureRTCRtpScriptTransform = async(streamTransformContext: StreamTransformContext) => {
      // Safari and Firefox ...
      const configureRTCRtpScriptTransformForTrack = async(context: StreamTransformContextTrack) => {
        const {receiver: trackReceiver, mediaStreamTrack: trackMediaStreamTrack, transformPipelineWorker: trackTransformPipelineWorker} = context;

        if (trackReceiver && trackMediaStreamTrack && trackTransformPipelineWorker) {
          trackReceiver.transform = new RTCRtpScriptTransform(trackTransformPipelineWorker, {name: `${trackMediaStreamTrack.kind}-transform-pipeline`});

          return new Promise<void>(resolve => {
            trackTransformPipelineWorker.addEventListener('message', ({data: message}: MessageEvent) => {
              if (message.type === TransformMessageType.PipelineConfigured) {
                resolve();
              }
            }, {once: true});
          });
        }

        return Promise.resolve();
      };

      const {videoTrack, audioTrack} = streamTransformContext;

      await Promise.all([configureRTCRtpScriptTransformForTrack(videoTrack), configureRTCRtpScriptTransformForTrack(audioTrack)]);
    };

    const configureEncodedStreams = async(streamTransformContext: StreamTransformContext) => {
      const configureEncodedStreamsForTrack = (context: StreamTransformContextTrack) => {
        const {receiver: trackReceiver, mediaStreamTrack: trackMediaStreamTrack, transformPipelineWorker: trackTransformPipelineWorker} = context;

        if (trackReceiver && trackMediaStreamTrack && trackTransformPipelineWorker) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - createEncodedStreams method is not defined in lib.dom.d.ts
          const encodedReceiverStreams = trackReceiver.createEncodedStreams();

          return new Promise<void>(resolve => {
            trackTransformPipelineWorker.addEventListener('message', ({data: message}: MessageEvent) => {
              if (message.type === TransformMessageType.PipelineConfigured) {
                resolve();
              }
            }, {once: true});

            trackTransformPipelineWorker.postMessage(
              {
                type: TransformMessageType.ConfigurePipeline,
                data: {
                  source: encodedReceiverStreams.readable,
                  sink: encodedReceiverStreams.writable
                }
              },
              {transfer: [encodedReceiverStreams.readable, encodedReceiverStreams.writable]}
            );
          });
        }

        return Promise.resolve();
      };

      const {videoTrack, audioTrack} = streamTransformContext;
      const actions: Promise<void>[] = [];

      if (videoTrack.receiver && videoTrack.transformPipelineWorker) {
        actions.push(configureEncodedStreamsForTrack(videoTrack));
      }

      if (audioTrack.receiver && audioTrack.transformPipelineWorker) {
        actions.push(configureEncodedStreamsForTrack(audioTrack));
      }

      await Promise.all(actions);
    };

    if (FeatureEnablement.isRTCRtpScriptTransformSupported) {
      // Safari and Firefox...
      await configureRTCRtpScriptTransform(streamTransformContext);

      return;
    }

    // Chrome, Chromium...
    await configureEncodedStreams(streamTransformContext);
  }

  static configureInsertableStreamTransformation(
    stream: MediaStream,
    videoStreamTransformCallback: IStreamTrackTransform<VideoFrame> | undefined,
    audioStreamTransformCallback: IStreamTrackTransform<AudioData> | undefined
  ): {transformedStream: MediaStream; disposables: DisposableList} {
    const transformedStream = new MediaStream();
    const disposables: DisposableList = new DisposableList();

    stream.getTracks().forEach(track => {
      const kind: MediaStreamTrackKind = track.kind as MediaStreamTrackKind;

      switch (kind) {
        case 'video': {
          if (videoStreamTransformCallback) {
            const insertableStreamTransformCallback: TransformerTransformCallback<VideoFrame, VideoFrame> = (chunk, controller) => {
              videoStreamTransformCallback(track, chunk, controller);
            };

            const videoTrack = track as MediaStreamVideoTrack;
            const transformer = new TransformStream({transform: insertableStreamTransformCallback});
            const processor = new MediaStreamTrackProcessor({track: videoTrack});
            const generator = new MediaStreamTrackGenerator({kind: videoTrack.kind});
            const source = processor.readable;
            const sink = generator.writable;

            source.pipeThrough(transformer).pipeTo(sink);

            transformedStream.addTrack(generator);
            disposables.add(
              new Disposable(() => {
                transformedStream.removeTrack(generator);
              })
            );
          } else {
            transformedStream.addTrack(track);
          }

          break;
        }

        case 'audio': {
          if (audioStreamTransformCallback) {
            const insertableStreamTransformCallback: TransformerTransformCallback<AudioData, AudioData> = (chunk, controller) => {
              audioStreamTransformCallback(track, chunk, controller);
            };

            const audioTrack = track as MediaStreamAudioTrack;
            const transformer = new TransformStream({transform: insertableStreamTransformCallback});
            const processor = new MediaStreamTrackProcessor({track: audioTrack});
            const generator = new MediaStreamTrackGenerator({kind: audioTrack.kind});
            const source = processor.readable;
            const sink = generator.writable;

            source.pipeThrough(transformer).pipeTo(sink);

            transformedStream.addTrack(generator);

            const mediaStream = new MediaStream();
            const audioPumper = new Audio();

            mediaStream.addTrack(track);
            audioPumper.srcObject = mediaStream;
            document.body.appendChild(audioPumper);
            disposables.add(
              new Disposable(() => {
                transformedStream.removeTrack(generator);
                document.body.removeChild(audioPumper);
              })
            );
          } else {
            transformedStream.addTrack(track);
          }

          break;
        }

        default:
          assertUnreachable(kind);
      }
    });

    return {
      transformedStream,
      disposables
    };
  }

  constructor() {
    throw new Error('InsertableStreams is a static class that may not be instantiated');
  }
}